import { logger } from "@/src/lib/logging/logger";

type FetchParameters = Parameters<typeof fetch>;

type FetchInput = FetchParameters[0];
type FetchRequestInit = FetchParameters[1];

export function isNotFound(error: Error) {
  if (error instanceof FetchError) {
    return error.statusCode === 404;
  }

  return false;
}

export class FetchError extends Error {
  statusCode: number;

  constructor(messsage: string, statusCode: number) {
    super(messsage);

    this.message = messsage;

    this.statusCode = statusCode;
  }
}

const VALID_REDIRECT_PATHS = new Set(["/login", "/unauthorized", "/authenticate"]);

function redirectToLogin() {
  if (typeof window === "undefined" || VALID_REDIRECT_PATHS.has(window.location.pathname)) {
    return;
  }

  const locationSearchParams = new URLSearchParams(window.location.search);
  const redirectSearchParams = new URLSearchParams({
    redirect: `${window.location.pathname}${locationSearchParams.size > 0 ? `?${locationSearchParams.toString()}` : ""}`,
  });

  window.location.href = `/login?${redirectSearchParams.toString()}`;
}

export async function fetchJSON<T extends object | null>(input: FetchInput, init?: FetchRequestInit): Promise<T> {
  const response = await fetch(input, init);

  if (response.status === 401) {
    logger.info("Unauthenticated API response, redirecting to login", { input });
    redirectToLogin();
    throw new FetchError("Unauthorized", response.status);
  }

  if (!response.ok) {
    let text = response.statusText;

    try {
      text = await response.text();
    } catch (_err) {
      // do nothing
    }

    logger.info("Invalid response", { status: response.status, input, text });
    throw new FetchError(text, response.status);
  }

  // handle redirects
  if (response.redirected) {
    const redirectUrl = new URL(response.url);

    if (!VALID_REDIRECT_PATHS.has(redirectUrl.pathname)) {
      // throw new FetchError(`Invalid redirect URL: ${redirectUrl.pathname}`, response.status);
      logger.info("Invalid redirect URL", { pathname: redirectUrl.pathname, input });

      // @ts-expect-error we need to kill logic here as page redirects
      return;
    }

    // Only redirect if we're not currently on a valid redirect path.
    if (!VALID_REDIRECT_PATHS.has(window.location.pathname)) {
      const locationSearchParams = new URLSearchParams(window.location.search);
      const redirectSearchParams = new URLSearchParams({
        redirect: `${window.location.pathname}${locationSearchParams.size > 0 ? `?${locationSearchParams.toString()}` : ""}`,
      });

      // We can't use redirect from next/navigation since it throws an error internally which will be caught by
      // react-query.
      window.location.href = `${redirectUrl.pathname}?${redirectSearchParams.toString()}`;
    }

    // @ts-expect-error we need to kill logic here as page redirects
    return;
  }

  if (response.status === 404) {
    throw new FetchError("Not found", response.status);
  }

  // handle bad request
  if (response.status < 200 || response.status >= 400) {
    let json: JSON | undefined;

    try {
      json = await response.json();
    } catch (_err) {
      // do nothing
    }

    logger.info("Invalid response status", { status: response.status, input, json });
    throw new FetchError(JSON.stringify(json), response.status);
  }

  // handle success
  return (await response.json()) as T;
}
